import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Package, Barcode, Truck, MapPin, FileText, Shield } from "lucide-react";
import { API } from "@/App";

export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        // Not authenticated, stay on landing
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: Barcode,
      title: "Instant Barcode Scanning",
      description: "Scan items in/out with phone camera or Bluetooth scanner",
    },
    {
      icon: Truck,
      title: "Supplier Management",
      description: "Track deliveries, manage suppliers, and catch discrepancies",
    },
    {
      icon: MapPin,
      title: "Multi-Location Tracking",
      description: "Warehouse, store floor, basement - track stock everywhere",
    },
    {
      icon: FileText,
      title: "Profit & ROI Analytics",
      description: "Cost, retail, wholesale prices with margin calculations",
    },
    {
      icon: Package,
      title: "Receiving Sessions",
      description: "Fast receiving workflow with invoice tracking",
    },
    {
      icon: Shield,
      title: "Audit Trail",
      description: "Full activity logs - who changed what and when",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 grid-background opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_ipinventory/artifacts/mxt23pqi_IMG_8675.png" 
                alt="IPVALUE Logo" 
                className="h-16 w-16 object-contain"
              />
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
                IPVALUE OS
              </h1>
            </div>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
              Smart Inventory Operating System
            </p>
            
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6 max-w-3xl mx-auto">
              Scan. Track. Profit.
              <br />
              <span className="text-primary">Inventory made fast.</span>
            </h2>
            
            <p className="text-muted-foreground max-w-xl mx-auto mb-10">
              Built for IPVALUE MANAGEMENTS GROUP & Ofezo Market. 
              Lightning-fast barcode scanning, multi-location tracking, 
              and real-time profit analytics.
            </p>
            
            <Button
              size="lg"
              onClick={handleLogin}
              className="font-semibold text-base px-8 py-6 rounded-sm"
              data-testid="login-btn"
            >
              Sign in with Google
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <p className="text-xs font-medium tracking-widest text-muted-foreground uppercase mb-3">
            Core Features
          </p>
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Everything you need to manage inventory
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 bg-card border border-border rounded-sm hover:border-primary/50 transition-colors"
            >
              <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                <feature.icon className="h-6 w-6 text-primary" />
              </div>
              <h4 className="font-heading font-semibold text-lg mb-2">{feature.title}</h4>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="relative h-64 lg:h-80 rounded-sm overflow-hidden border border-border">
            <img
              src="https://images.unsplash.com/photo-1739204618173-3e89def7140f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBpbnRlcmlvciUyMHNoZWx2ZXMlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzY1NzA2NzIzfDA&ixlib=rb-4.1.0&q=85"
              alt="Modern warehouse"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative h-64 lg:h-80 rounded-sm overflow-hidden border border-border">
            <img
              src="https://images.unsplash.com/photo-1715635845923-c9e13b646c49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxyZXRhaWwlMjBzdG9yZSUyMG93bmVyJTIwdXNpbmclMjB0YWJsZXQlMjBzY2FubmVyfGVufDB8fHx8MTc2NTcwNjcyNHww&ixlib=rb-4.1.0&q=85"
              alt="Retail store owner"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            IPVALUE MANAGEMENTS GROUP / Ofezo Market
          </p>
          <p className="text-sm text-muted-foreground">
            Open 7 days/week &middot; 9 AM – 9 PM Eastern Time
          </p>
          <p className="text-sm">
            <a href="mailto:support@ipvaluemanagements.com" className="text-primary hover:underline">
              support@ipvaluemanagements.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
